import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { getOperationName } from '@apollo/client/utilities';
import { BpfLinkType } from '@trnsact/trnsact-shared-types';

import {
  CreateBPFTemplateSchema,
  UpdateBPFTemplateSchema,
  UpdateStageFieldsSchema,
  CopyBPFTemplateSchema,
} from '../../model';
import {
  bpfAPI,
  UpdateStageFields,
  UpdateBPFVariables,
  CompleteStageResponse,
  CompleteStageVariables,
  UnCompleteStageResponse,
  UnCompleteStageVariables,
  CreateBPFTemplateInput,
  CopyBPFTemplateInput,
  CreateTemplateResponse,
  UpdateBPFTemplateInput,
  CreateTemplateVariables,
  CopyTemplateVariables,
  UpdateStageFieldsResponse,
  UpdateStageFieldsVariables,
  CompleteStageVariablesSchema,
  UnCompleteStageVariablesSchema,
  SetTemplateActiveStatusVariables,
  SetTemplateActiveStatusVariablesSchema,
  SetTemplateActiveStatusResponse,
  SetDefaultsForAccountResponse,
} from '../index';

export function useBPFMutations() {
  const { showNotification } = useNotifications();

  const [onUpdateDealCurrentStage, { loading: isDealStageUpdating }] = useMutation<void, UpdateBPFVariables>(
    bpfAPI.updateStageForDepartment,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        getOperationName(bpfAPI.fetchVOTemplatesAndStages)!,
        getOperationName(bpfAPI.fetchCSTemplatesAndStages)!,
      ],
    }
  );

  const [onCreateTemplate, { loading: isTemplateCreating }] = useMutation<
    CreateTemplateResponse,
    CreateTemplateVariables
  >(bpfAPI.createTemplate);

  const [onCopyTemplate, { loading: isTemplateCopying }] = useMutation<CreateTemplateResponse, CopyTemplateVariables>(
    bpfAPI.copyTemplate
  );

  const [onUpdateStageFields, { loading: isStagesUpdating }] = useMutation<
    UpdateStageFieldsResponse,
    UpdateStageFieldsVariables
  >(bpfAPI.updateStageFields, {
    awaitRefetchQueries: true,
    refetchQueries: [
      getOperationName(bpfAPI.fetchVOTemplatesAndStages)!,
      getOperationName(bpfAPI.fetchCSTemplatesAndStages)!,
    ],
  });

  const [onUpdateTemplate, { loading: isTemplateUpdating }] = useMutation<
    CreateTemplateResponse,
    CreateTemplateVariables
  >(bpfAPI.updateTemplate);

  const [onCompleteStage, { loading: isStageCompleting }] = useMutation<CompleteStageResponse, CompleteStageVariables>(
    bpfAPI.completeStage,
    {
      awaitRefetchQueries: true,
      refetchQueries: [
        getOperationName(bpfAPI.fetchVOTemplatesAndStages)!,
        getOperationName(bpfAPI.fetchCSTemplatesAndStages)!,
      ],
    }
  );

  const [onUnCompleteStage, { loading: isStageUnCompleting }] = useMutation<
    UnCompleteStageResponse,
    UnCompleteStageVariables
  >(bpfAPI.unCompleteStage, {
    awaitRefetchQueries: true,
    refetchQueries: [
      getOperationName(bpfAPI.fetchVOTemplatesAndStages)!,
      getOperationName(bpfAPI.fetchCSTemplatesAndStages)!,
    ],
  });

  const [onSetActiveStatus, { loading: isActiveStatusSetting }] = useMutation<
    SetTemplateActiveStatusResponse,
    SetTemplateActiveStatusVariables
  >(bpfAPI.setActiveStatus, {
    awaitRefetchQueries: true,
    refetchQueries: [bpfAPI.fetchTemplates],
  });

  const [onSetDefaultsForAccount, { loading: isDefaultsForAccountSetting }] =
    useMutation<SetDefaultsForAccountResponse>(bpfAPI.setDefaultsForAccount);

  const handleCreateTemplate = async (dto: CreateBPFTemplateInput) => {
    try {
      const { data, success, error } = CreateBPFTemplateSchema.safeParse(dto);

      if (!success) {
        logError('Zod parse error', error);
        return;
      }

      await onCreateTemplate({ variables: { input: data } });
      showNotification('Template created successfully.');
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const handleCopyTemplate = async (dto: CopyBPFTemplateInput) => {
    try {
      const { data, success, error } = CopyBPFTemplateSchema.safeParse(dto);

      if (!success) {
        logError('Zod parse error', error);
        return;
      }

      await onCopyTemplate({ variables: { input: data } });
      showNotification('Template copied successfully.');
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const handleUpdateTemplate = async (dto: UpdateBPFTemplateInput) => {
    try {
      const { data, success, error } = UpdateBPFTemplateSchema.safeParse(dto);

      if (!success) {
        logError('Zod parse error', error);
        return;
      }

      await onUpdateTemplate({ variables: { input: data } });
      showNotification('Template updated successfully.');
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const handleUpdateStageAttributes = async (dto: UpdateStageFields) => {
    try {
      const { data, success, error } = UpdateStageFieldsSchema.safeParse(dto);

      if (!success) {
        logError('Zod parse error', error);
        return;
      }

      await onUpdateStageFields({ variables: { input: data } });
      showNotification('Stage Attribute updated successfully.');
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const handleCompleteStage = async (stageId: string, parentId: string, parentType: BpfLinkType) => {
    try {
      const {
        error,
        success,
        data: variables,
      } = CompleteStageVariablesSchema.safeParse({
        parentId,
        parentType,
        input: { stageId, parentId, parentType },
      });

      if (!success) {
        logError('Zod parse error', error);
        return null;
      }

      const { data, errors } = await onCompleteStage({ variables });

      if (!data?.bpf?.result?.entity || !data?.bpf?.result?.success || errors) {
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        logError(error);
        return null;
      }

      showNotification('Stage completed successfully.');

      return { success: data.bpf.result.success, data: data.bpf.result.entity };
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleUnCompleteStage = async (stageId: string, parentId: string, parentType: BpfLinkType) => {
    try {
      const {
        error,
        success,
        data: variables,
      } = UnCompleteStageVariablesSchema.safeParse({
        parentId,
        parentType,
        input: { stageId, parentId, parentType },
      });

      if (!success) {
        logError('Zod parse error', error);
        return null;
      }

      const { data, errors } = await onUnCompleteStage({ variables });

      if (!data?.bpf?.result?.entity || !data?.bpf?.result?.success || errors) {
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        logError(error);
        return null;
      }

      showNotification('Stage reopened successfully.');

      return { success: data.bpf.result.success, data: data.bpf.result.entity };
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
      return null;
    }
  };

  const handleSetActiveStatus = async (flowId: string, departmentId: string) => {
    try {
      const {
        error,
        success,
        data: variables,
      } = SetTemplateActiveStatusVariablesSchema.safeParse({ input: { flowId, departmentId } });

      if (!success) {
        logError('Zod parse error', error);
      }

      const { data, errors } = await onSetActiveStatus({ variables });

      if (!data?.bpf?.result?.success || data?.bpf?.result?.error || errors) {
        showNotification(data?.bpf?.result?.error ?? 'Something went wrong', { type: 'error' });
        logError(error);
      } else {
        showNotification('Updated template active status');
      }
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  const handleSetDefaultsForAccount = async () => {
    try {
      const { data, errors } = await onSetDefaultsForAccount();

      if (data?.bpf?.result?.success || data?.bpf?.result?.error || errors) {
        showNotification('Defaults for account set successfully.');
      }
    } catch (error) {
      showNotification('Something went wrong', { type: 'error' });
      logError(error);
    }
  };

  return {
    isStagesUpdating,
    isStageCompleting,
    isStageUnCompleting,
    isTemplateUpdating,
    isTemplateCreating,
    isTemplateCopying,
    isActiveStatusSetting,
    handleCompleteStage,
    handleUnCompleteStage,
    handleCreateTemplate,
    handleCopyTemplate,
    handleUpdateTemplate,
    handleSetActiveStatus,
    handleSetDefaultsForAccount,
    isDefaultsForAccountSetting,
    onUpdateDealCurrentStage,
    handleUpdateStageAttributes,
    isBPFsUpdating: isDealStageUpdating,
  };
}
